import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, ExternalLink, Calendar, Clock, CheckCircle2, 
  FileText, ListChecks, Share2, MoreVertical, Trash2, Edit,
  Sparkles, AlertTriangle, User, Loader2, Pencil, CalendarIcon, X
} from "lucide-react";
import { format, differenceInDays, isPast } from "date-fns";
import { fr } from "date-fns/locale";
import { db } from "@/lib/supabaseUntyped";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { DemarcheDocumentsList } from "@/components/demarches/DemarcheDocumentsList";
import { DemarcheStepsList } from "@/components/demarches/DemarcheStepsList";
import { SourceDocumentCard } from "@/components/demarches/SourceDocumentCard";
import { DemarcheExportBar } from "@/components/demarches/DemarcheExportBar";
import { getDemarcheTemplate, DEMARCHE_CATEGORIES } from "@/config/demarchesConfig";

interface DemarcheDocument {
  id: string;
  label: string;
  doc_type: string | null;
  is_required: boolean;
  is_completed: boolean;
  validity_rule: string | null;
  document_id: string | null;
  sort_order: number;
  document?: {
    id: string;
    title: string;
    file_name: string;
    file_path: string;
    file_size: number | null;
    mime_type: string;
    expires_at: string | null;
  } | null;
}

interface DemarcheStep {
  id: string;
  label: string;
  step_type: string;
  is_completed: boolean;
  due_date: string | null;
  url: string | null;
  notes: string | null;
  sort_order: number;
  completed_at: string | null;
}

interface Demarche {
  id: string;
  title: string;
  description: string | null;
  template_key: string | null;
  status: string;
  due_date: string | null;
  completed_at: string | null;
  auto_generated: boolean;
  official_url: string | null;
  for_member_id: string | null;
  created_at: string;
  for_member?: {
    first_name: string;
    last_name: string;
  } | null;
}

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-amber-500', textColor: 'text-amber-600' },
  in_progress: { label: 'En cours', color: 'bg-blue-500', textColor: 'text-blue-600' },
  completed: { label: 'Terminée', color: 'bg-green-500', textColor: 'text-green-600' },
  cancelled: { label: 'Annulée', color: 'bg-gray-500', textColor: 'text-gray-600' },
};

export default function DemarcheDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [demarche, setDemarche] = useState<Demarche | null>(null);
  const [documents, setDocuments] = useState<DemarcheDocument[]>([]);
  const [steps, setSteps] = useState<DemarcheStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editDueDate, setEditDueDate] = useState<Date | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);

  const fetchDemarche = async () => {
    if (!id) return;

    try {
      // Fetch demarche (using 'as any' until types are regenerated)
      const { data: demarcheData, error: demarcheError } = await db
        .from('demarches')
        .select(`
          *,
          for_member:family_members(first_name, last_name)
        `)
        .eq('id', id)
        .single();

      if (demarcheError) throw demarcheError;
      setDemarche(demarcheData);

      // Fetch documents
      const { data: docsData, error: docsError } = await db
        .from('demarche_documents')
        .select(`
          *,
          document:documents(id, title, file_name, file_path, file_size, mime_type, expires_at)
        `)
        .eq('demarche_id', id)
        .order('sort_order');

      if (docsError) throw docsError;
      setDocuments(docsData || []);

      // Fetch steps
      const { data: stepsData, error: stepsError } = await db
        .from('demarche_steps')
        .select('*')
        .eq('demarche_id', id)
        .order('sort_order');

      if (stepsError) throw stepsError;
      setSteps(stepsData || []);
    } catch (error) {
      console.error('Error fetching demarche:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDemarche();
  }, [id]);

  const handleDelete = async () => {
    if (!id) return;

    try {
      const { error } = await db
        .from('demarches')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Démarche supprimée');
      navigate('/demarches');
    } catch (error) {
      console.error('Error deleting demarche:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!id) return;

    try {
      const updates: any = { status: newStatus };
      if (newStatus === 'completed') {
        updates.completed_at = new Date().toISOString();
      } else {
        updates.completed_at = null;
      }

      const { error } = await db
        .from('demarches')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      setDemarche(prev => prev ? { ...prev, ...updates } : null);
      toast.success('Statut mis à jour');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleOpenEditDialog = () => {
    if (demarche) {
      setEditTitle(demarche.title);
      setEditDescription(demarche.description || "");
      setEditDueDate(demarche.due_date ? new Date(demarche.due_date) : undefined);
      setEditDialogOpen(true);
    }
  };

  const handleSaveEdit = async () => {
    if (!id || !editTitle.trim()) {
      toast.error("Le titre est obligatoire");
      return;
    }

    setIsSaving(true);
    try {
      const updates: any = {
        title: editTitle.trim(),
        description: editDescription.trim() || null,
        due_date: editDueDate ? editDueDate.toISOString() : null,
      };

      const { error } = await db
        .from('demarches')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      setDemarche(prev => prev ? { ...prev, ...updates } : null);
      setEditDialogOpen(false);
      toast.success('Démarche modifiée');
    } catch (error) {
      console.error('Error updating demarche:', error);
      toast.error('Erreur lors de la modification');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container max-w-4xl mx-auto px-4 py-6">
        <Skeleton className="h-8 w-48 mb-6" />
        <Skeleton className="h-32 rounded-xl mb-6" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  if (!demarche) {
    return (
      <div className="container max-w-4xl mx-auto px-4 py-16 text-center">
        <p className="text-muted-foreground">Démarche introuvable</p>
        <Button variant="link" onClick={() => navigate('/demarches')}>
          Retour aux démarches
        </Button>
      </div>
    );
  }

  const template = demarche.template_key ? getDemarcheTemplate(demarche.template_key) : null;
  const category = template ? DEMARCHE_CATEGORIES[template.category] : null;
  const statusConfig = STATUS_CONFIG[demarche.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.pending;

  // Progress calculation
  const totalItems = documents.length + steps.length;
  const completedItems = documents.filter(d => d.is_completed).length + steps.filter(s => s.is_completed).length;
  const progressPercent = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  // Due date info
  const dueDate = demarche.due_date ? new Date(demarche.due_date) : null;
  const daysUntilDue = dueDate ? differenceInDays(dueDate, new Date()) : null;
  const isOverdue = dueDate && isPast(dueDate) && demarche.status !== 'completed';

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-4xl mx-auto px-4 py-6 pb-24 md:pb-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {category && (
                <Badge variant="outline" className="text-xs">
                  {category.label}
                </Badge>
              )}
              {demarche.auto_generated && (
                <Badge variant="secondary" className="text-xs gap-1">
                  <Sparkles className="w-3 h-3" />
                  Auto
                </Badge>
              )}
            </div>
            <h1 className="text-xl font-bold truncate">{demarche.title}</h1>
            {demarche.for_member && (
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                <User className="w-3 h-3" />
                Pour {demarche.for_member.first_name} {demarche.for_member.last_name}
              </p>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleOpenEditDialog}>
                <Pencil className="w-4 h-4 mr-2" />
                Modifier
              </DropdownMenuItem>
              {demarche.official_url && (
                <DropdownMenuItem asChild>
                  <a href={demarche.official_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Site officiel
                  </a>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => handleStatusChange('pending')}
                disabled={demarche.status === 'pending'}
              >
                <Clock className="w-4 h-4 mr-2" />
                Marquer en attente
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleStatusChange('in_progress')}
                disabled={demarche.status === 'in_progress'}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Marquer en cours
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleStatusChange('completed')}
                disabled={demarche.status === 'completed'}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Marquer terminée
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => setDeleteDialogOpen(true)}
                className="text-destructive"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Status and progress card */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${statusConfig.color}`} />
                <span className={`font-medium ${statusConfig.textColor}`}>
                  {statusConfig.label}
                </span>
              </div>
              {dueDate && (
                <div className={`flex items-center gap-2 text-sm ${isOverdue ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {isOverdue ? (
                    <AlertTriangle className="w-4 h-4" />
                  ) : (
                    <Calendar className="w-4 h-4" />
                  )}
                  <span>
                    {isOverdue 
                      ? `En retard de ${Math.abs(daysUntilDue!)} jours`
                      : daysUntilDue === 0 
                        ? "Aujourd'hui"
                        : `Dans ${daysUntilDue} jours`
                    }
                  </span>
                </div>
              )}
            </div>

            {totalItems > 0 && (
              <div>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Progression</span>
                  <span className="font-medium">{progressPercent}%</span>
                </div>
                <Progress value={progressPercent} className="h-2" />
                <p className="text-xs text-muted-foreground mt-2">
                  {completedItems} sur {totalItems} éléments complétés
                </p>
              </div>
            )}

            {demarche.description && (
              <p className="text-sm text-muted-foreground mt-4 pt-4 border-t">
                {demarche.description}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Export bar - only show when at least one document is linked */}
        {(() => {
          const linkedDocuments = documents.filter(
            (d) => d.document_id && d.document
          ).map((d) => ({
            id: d.id,
            label: d.label,
            doc_type: d.doc_type,
            document_id: d.document_id!,
            sort_order: d.sort_order,
            document: {
              id: d.document!.id,
              title: d.document!.title,
              file_name: d.document!.file_name,
              file_path: d.document!.file_path,
              file_size: d.document!.file_size,
              mime_type: d.document!.mime_type,
              expires_at: d.document!.expires_at,
            },
          }));
          
          return linkedDocuments.length > 0 ? (
            <DemarcheExportBar
              demarcheTitle={demarche.title}
              demarcheId={demarche.id}
              linkedDocuments={linkedDocuments}
            />
          ) : null;
        })()}

        {/* Source document card */}
        <SourceDocumentCard demarcheId={demarche.id} />

        {/* Documents section */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              Documents requis
              <Badge variant="secondary" className="ml-auto">
                {documents.filter(d => d.is_completed).length}/{documents.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <DemarcheDocumentsList
              demarcheId={demarche.id}
              documents={documents}
              onUpdate={fetchDemarche}
            />
          </CardContent>
        </Card>

        {/* Steps section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ListChecks className="w-4 h-4 text-primary" />
              Étapes à suivre
              <Badge variant="secondary" className="ml-auto">
                {steps.filter(s => s.is_completed).length}/{steps.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <DemarcheStepsList
              demarcheId={demarche.id}
              steps={steps}
              onUpdate={fetchDemarche}
            />
          </CardContent>
        </Card>
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette démarche ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Tous les documents liés et étapes seront également supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary" />
              Modifier la démarche
            </DialogTitle>
          <DialogDescription>
              Modifiez les informations de cette démarche
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="editTitle">Titre</Label>
              <Input
                id="editTitle"
                placeholder="Titre de la démarche"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editDescription">Description (optionnel)</Label>
              <Textarea
                id="editDescription"
                placeholder="Description de la démarche..."
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Date limite (optionnel)</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !editDueDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {editDueDate ? (
                      format(editDueDate, "d MMMM yyyy", { locale: fr })
                    ) : (
                      "Sélectionner une date limite"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={editDueDate}
                    onSelect={setEditDueDate}
                    initialFocus
                    locale={fr}
                  />
                </PopoverContent>
              </Popover>
              {editDueDate && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-muted-foreground"
                  onClick={() => setEditDueDate(undefined)}
                >
                  <X className="w-3 h-3 mr-1" />
                  Supprimer la date limite
                </Button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveEdit} disabled={isSaving || !editTitle.trim()}>
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                "Enregistrer"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
